import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { money } from "@/lib/money";
import { toast } from "sonner";
import { ArrowLeft, PencilSimple, Trash } from "@phosphor-icons/react";
import NewPacketModal from "@/components/NewPacketModal";
import { DraftBanner, DraftStatusPill, REVIEWABLE } from "./shared";

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[150px_1fr] gap-3 py-2 border-b border-slate-100 last:border-0 text-sm">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 pt-0.5">{label}</div>
      <div className="text-slate-800">{value ?? "—"}</div>
    </div>
  );
}

export default function SalesPacketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { autoSend } = useOutletContext();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  const [editing, setEditing] = useState(false);
  const load = () => api.get(`/sales/packets/${id}`).then((r) => (r.data ? setP(r.data) : setMissing(true))).catch(() => setMissing(true));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (missing) return <div className="text-slate-500">This packet isn't one of yours (or was removed). <Link to="/sales/packets" className="underline">Back to packets</Link></div>;
  if (!p) return <div className="text-slate-500">Loading…</div>;
  const reviewable = REVIEWABLE.includes(p.status);

  const withdraw = async () => {
    if (!window.confirm(`Withdraw the packet for ${p.worker_email}?`)) return;
    try { await api.delete(`/sales/packets/${id}`); toast.success("Packet withdrawn."); nav("/sales/packets"); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };

  return (
    <div data-testid="sales-packet-detail">
      <Link to="/sales/packets" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3" data-testid="sales-packet-back"><ArrowLeft size={14} weight="bold" /> All assignment packets</Link>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="tinylabel text-emerald-700">Assignment packet</div>
          <h1 className="text-3xl font-black">{p.worker_name || p.worker_email}</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap"><DraftStatusPill doc={p} /><span className="text-xs text-slate-500">→ {p.project_name || p.company_name}</span>
            {p.assignment_id_created && <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-emerald-100 text-emerald-800 border-emerald-300">assigned</span>}
          </div>
        </div>
        {reviewable && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setEditing(true)} data-testid="sales-packet-edit-btn" className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold text-sm inline-flex items-center gap-2"><PencilSimple size={16} weight="bold" /> Edit &amp; resubmit</button>
            <button onClick={withdraw} data-testid="sales-packet-withdraw-btn" className="h-10 px-3 border border-slate-300 rounded-md font-semibold text-sm inline-flex items-center gap-2 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 bg-white"><Trash size={16} weight="bold" /> Withdraw</button>
          </div>
        )}
      </div>

      <div className="mb-4"><DraftBanner doc={p} kind="packet" onEdit={() => setEditing(true)} /></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <div className="tinylabel mb-2">Worker</div>
          <Row label="Email" value={p.worker_email} />
          <Row label="Name" value={p.worker_name} />
          <Row label="Phone" value={p.worker_phone} />
          <Row label="Type" value={p.worker_id ? "Returning contractor (paperwork on file)" : "New contractor (full onboarding)"} />
          <div className="tinylabel mt-5 mb-2">Project</div>
          <Row label="Project" value={p.project_name} />
          <Row label="Client" value={p.company_name} />
          <Row label="Location" value={p.location} />
          <Row label="Scope" value={<span className="whitespace-pre-line">{p.description}</span>} />
          <Row label="Site contact" value={`${p.contact_name || "—"} · ${p.contact_phone || "—"}`} />
        </section>
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <div className="tinylabel mb-2">Pay terms</div>
          <Row label="Pay rate" value={<span className="font-mono">{money(p.pay_rate)} / hr</span>} />
          {p.per_diem_applies && <Row label="Per diem" value={<span className="font-mono">{money(p.per_diem_rate)} / day</span>} />}
          {p.ot_rate_applies && <Row label="Overtime" value={<span className="font-mono">{money(p.ot_rate)} / hr after {p.ot_threshold_hours || 40} hrs</span>} />}
          {p.holiday_rate_applies && <Row label="Holiday pay" value={<span className="font-mono">{money(p.holiday_pay_rate)} / hr</span>} />}
          <Row label="Start · duration" value={`${p.anticipated_start_date || "TBD"} · ${p.anticipated_duration || "TBD"}`} />
          <Row label="Auto-assign" value={p.auto_assign ? "Yes — assignment created when the worker signs" : "No — office creates the assignment"} />
          <Row label="Submitted" value={p.submitted_at ? String(p.submitted_at).slice(0, 10) : "—"} />
          {p.reviewed_at && <Row label="Reviewed" value={`${String(p.reviewed_at).slice(0, 10)}${p.reviewed_by ? ` by ${p.reviewed_by}` : ""}`} />}
          {p.completed_at && <Row label="Signed" value={String(p.completed_at).slice(0, 10)} />}
        </section>
      </div>

      {editing && <NewPacketModal mode="sales" autoSend={autoSend} initial={p} onClose={() => setEditing(false)} onSent={load} />}
    </div>
  );
}
